import { Formik, FormikHelpers, FormikProps } from "formik";
import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { Button } from "@/components/Button";
import { FormikError, VALIDATION_TYPES } from "@/components/Formik";
import { Group } from "@/components/Group";
import { Text } from "@/components/Text";
import { TEmailNotificationFrequency } from "@/data/job-requests";
import { Flex } from "@/eworkitComponents";
import { FormikForm, FormikTextInput } from "@/formikComponents";
import { ICodelistGenericSelectParams } from "@/views/components/Codelists";
import { FormikCodelistGenericSelect } from "@/views/components/Formik";

const EMAIL_NOTIFICATION_FREQUENCIES_CODELIST_QUERY: ICodelistGenericSelectParams = {
  codelistName: "filter-email-frequency",
};

export interface SaveSearchFormValues {
  name: string;
  frequency: TEmailNotificationFrequency;
}

const FORM_INITIAL_VALUES: SaveSearchFormValues = {
  name: "",
  frequency: "DAILY",
};

const SEARCH_NAME_MAX_LENGTH = 255;

const validationSchema = Yup.object().shape({
  name: Yup.string().required(VALIDATION_TYPES.REQUIRED).max(SEARCH_NAME_MAX_LENGTH, VALIDATION_TYPES.MAX_LENGTH),
  frequency: Yup.string().required(VALIDATION_TYPES.REQUIRED),
});

interface SaveSearchFormProps {
  form: FormikProps<SaveSearchFormValues>;
  onCancel: () => void;
  onDelete: () => void;
  showDelete?: boolean;
}

export const SaveSearchForm: FC<SaveSearchFormProps> = ({ form, onCancel, onDelete, showDelete }) => {
  const { t } = useTranslation("session");

  return (
    <FormikForm>
      <Flex direction="column" innerMargin="lg">
        <Group direction="column" label={t("JOB_REQUESTS.LIST.FILTERS.FILTER_NAME")} width={12}>
          <FormikTextInput
            type="text"
            name="name"
            value={form.values.name}
            required
            size="lg"
            placeholder={t("JOB_REQUESTS.LIST.FILTERS.FILTER_NAME_PLACEHOLDER")}
          />
          <FormikError
            name="name"
            overrideMessages={{
              MAX_LENGTH: t("common:VALIDATION_MESSAGES.MAX_LENGTH", { value: SEARCH_NAME_MAX_LENGTH }),
            }}
          />
        </Group>

        <Flex direction="column" innerMargin="xs">
          <Group direction="column" label={t("JOB_REQUESTS.LIST.FILTERS.FILTER_NOTIFICATION_FREQUENCY")} width={12}>
            <FormikCodelistGenericSelect
              name="frequency"
              query={EMAIL_NOTIFICATION_FREQUENCIES_CODELIST_QUERY}
              required
              simpleValue
              value={form.values.frequency}
              size="lg"
              isClearable={false}
            />
            <FormikError name="frequency" />
          </Group>
          <Text size="sm" color="grey1">
            {t("JOB_REQUESTS.LIST.FILTERS.FILTER_NOTIFICATION_INFO")}
          </Text>
        </Flex>

        <Flex direction="row" justify="space-between">
          <Flex direction="row" justify="flex-start">
            {showDelete && (
              <Button variant="secondary" icon="trash" onClick={onDelete} size="lg">
                {t("JOB_REQUESTS.LIST.FILTERS.BUTTON_DELETE")}
              </Button>
            )}
          </Flex>

          <Flex direction="row" justify="flex-end" innerMargin="md">
            <Button variant="secondary" onClick={onCancel} size="lg">
              {t("JOB_REQUESTS.LIST.FILTERS.BUTTON_CANCEL")}
            </Button>
            <Button disabled={form.isSubmitting} onClick={form.submitForm} size="lg">
              {t("JOB_REQUESTS.LIST.FILTERS.BUTTON_SAVE")}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </FormikForm>
  );
};

interface ISaveSearchFormikProps extends Omit<SaveSearchFormProps, "form"> {
  initialValues?: SaveSearchFormValues;
  onSubmit: (values: SaveSearchFormValues, actions: FormikHelpers<SaveSearchFormValues>) => Promise<void>;
}

export const SaveSearchFormik: FC<ISaveSearchFormikProps> = memo(
  ({ initialValues = FORM_INITIAL_VALUES, onSubmit, ...rest }) => (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {form => <SaveSearchForm form={form} {...rest} />}
    </Formik>
  ),
);
